import { Dispatch, SetStateAction } from "react";
import { cn, uuid } from "@/utils/string.ts";
import { BookExamsToDo } from "@/types";
import { useTranslation } from "react-i18next";
import Button from "@edusogno/primitives/button.js";
import CheckGreen from "../icons/CheckGreen";
import NewFileIcon from "../icons/book-exam/NewFileIcon";
import FilePngIcon from "../icons/book-exam/FilePngIcon";
import FileJpegIcon from "../icons/book-exam/FileJpegIcon";
import LoadingButton from "@edusogno/primitives/loading-button.js";
import CheckSuccessGreen from "../icons/CheckSuccessGreen";
import britishCouncilImg from "@/assets/img/british-council.webp";

interface MobileViewProps {
  toDo: BookExamsToDo[];
  steps: string[];
  photo: FileList | null;
  deadline: Date;
  onSubmit: () => void;
  setPhoto: Dispatch<SetStateAction<FileList | null>>;
  isLoading: boolean;
  completed: number[];
  currentStep: number;
  setCompleted: Dispatch<SetStateAction<number[]>>;
  isPastKickObj: boolean;
  setCurrentStep: Dispatch<SetStateAction<number>>;
  bookingCompleted: boolean;
}

export default function MobileView({
  toDo,
  steps,
  photo,
  setPhoto,
  onSubmit,
  completed,
  isLoading,
  currentStep,
  setCompleted,
  setCurrentStep,
  bookingCompleted,
}: MobileViewProps) {
  const { t } = useTranslation();

  return (
    <div className="flex flex-col gap-2 grow">
      <div className="w-full py-2 rounded-sm">
        <ul className="grid grid-cols-2 gap-x-2 justify-center">
          {steps.map((el, idx) => (
            <li key={uuid("book-exam-options")} className="flex">
              <button
                disabled={
                  (currentStep <= idx && !completed?.includes(idx)) ||
                  bookingCompleted
                }
                className={cn(
                  "w-full bg-brand-light text-center text-[10px] font-semibold p-4 rounded-md",
                  {
                    "bg-shade-five":
                      completed?.length > 0 && completed.includes(idx),
                    "disabled:opacity-40":
                      currentStep < idx || currentStep === steps?.length,
                  },
                )}
                onClick={() => {
                  if (idx == 0) {
                    setCompleted([]);
                  }

                  setCurrentStep(idx);
                }}
              >
                {el}
              </button>
            </li>
          ))}
        </ul>
      </div>

      <div className="bg-white w-full h-full p-5 grow rounded-sm">
        {!currentStep ? (
          <div className="flex flex-col gap-2 h-full">
            <div>
              <h3 className="text-brand-secondary text-xs">
                {t("book_exam.steps.one.title")}
              </h3>

              <p className="text-xs leading-4 font-semibold mb-4">
                {t("book_exam.steps.one.subtitle")}
              </p>
            </div>

            <div className="bg-brand-lavender rounded-md p-4 flex flex-col gap-4 justify-centers items-center flex-nowrap">
              <p className="text-center text-[10px] leading-4">
                {t("book_exam.steps.one.info")}
              </p>

              <Button
                size="md"
                variant="contained"
                onClick={() => {
                  const userLanguage = localStorage.getItem("i18nextLng");

                  let bookingLink = "https://ieltsregistration.britishcouncil.org/register-for-agent/ZWZmMzk2MTctMTFhNi00ODFiLTkzYzItZWE0MmRhZTUwNTRlOzE3MDIzOy0zMDU1ODc4MjQ=";

                  if (userLanguage === "de") {
                    bookingLink =
                      "https://www.britishcouncil.de/en/exam/ielts/book-test";
                  } else if (userLanguage === "es") {
                    bookingLink =
                      "https://www.britishcouncil.es/en/exam/ielts/book-test";
                  }

                  window.open(bookingLink, "_blank");
                }}
                className="whitespace-nowrap"
              >
                {t("btns.book_exam")}
              </Button>
            </div>

            <p className="font-semibold text-xs">
              {t("book_exam.subtitle", { defaultValue: "You need to insert:" })}
            </p>

            <ul className="flex flex-col gap-2">
              {toDo.map((el) => (
                <li className="bg-light-350 rounded-md p-4 flex gap-6 h-14 items-center justify-start flex-nowrap">
                  <i className="w-6 h-6 flex-shrink-0">
                    <el.icon className="w-full h-full" />
                  </i>

                  <p className="text-brand-black text-[10px] leading-none">
                    {el.text}
                  </p>
                </li>
              ))}
            </ul>

            <Button
              size="lg"
              variant="contained"
              onClick={() => {
                setCompleted([currentStep]);
                setCurrentStep((r) => r + 1);
              }}
              className="whitespace-nowrap mx-auto max-w-[400px] mt-auto"
            >
              {t("book_exam.steps.btns.i_have_booked")}
            </Button>
          </div>
        ) : currentStep == 1 ? (
          <div className="flex flex-col gap-2 h-full">
            <div>
              <h3 className="text-brand-secondary text-xs">
                {t("book_exam.steps.three.title")}
              </h3>

              <p className="text-xs font-semibold mb-2">
                {t("book_exam.steps.three.subtitle")}
              </p>
            </div>

            <p className="font-normal text-[10px]">
              {t("book_exam.steps.three.info")}
            </p>

            <div className="flex flex-col gap-4">
              <div className="flex-1 ">
                <img
                  src={britishCouncilImg}
                  alt="Ielts organisation example"
                  className="object-cover w-full object-center"
                />
              </div>
              <div className="bg-light-350 w-full rounded-md grow max-h-40 py-4 flex-1 flex justify-center items-center">
                <input
                  id="ielts-photo"
                  type="file"
                  name="ielts-photo"
                  onChange={(el) => setPhoto(el.target.files)}
                  className="sr-only"
                />
                <label
                  htmlFor="ielts-photo"
                  className="flex flex-col gap-1 items-center cursor-pointer"
                >
                  {!photo && <NewFileIcon className="h-6 w-6" />}
                  {photo && photo[0].type.toLowerCase().includes("jpeg") && (
                    <FileJpegIcon className="h-6 w-6" />
                  )}
                  {photo && photo[0].type.toLowerCase().includes("png") && (
                    <FilePngIcon className="h-6 w-6" />
                  )}
                  <p
                    className={cn("text-light-900 font-semibold text-xs", {
                      "text-brand-black": photo,
                    })}
                  >
                    {photo
                      ? photo[0].name
                      : t("book_exam.steps.btns.click_upload")}
                  </p>
                  {photo && (
                    <p className="text-brand-deep font-semibold flex gap-1.5 items-center">
                      <CheckGreen />
                      {t("book_exam.steps.btns.upload_complete")}
                    </p>
                  )}
                </label>
              </div>
            </div>

            <div className="flex gap-4 w-full max-w-[400px] mx-auto mt-auto">
              <Button
                size="md"
                variant="contained"
                onClick={() => {
                  setPhoto(null);
                  setCompleted(completed.slice(0, -1));
                  setCurrentStep((r) => r - 1);
                }}
                className="whitespace-nowrap bg-white text-edu-black"
              >
                {t("btns.back")}
              </Button>

              <LoadingButton
                size="md"
                loading={isLoading}
                variant="contained"
                onClick={() => {
                  setCompleted([...completed, currentStep]);
                  setCurrentStep((r) => r + 1);
                  onSubmit();
                }}
                disabled={!photo}
                className="whitespace-nowrap bg-shade-five text-brand-black"
              >
                {t("btns.confirm")}
              </LoadingButton>
            </div>
          </div>
        ) : (
          currentStep == 2 && (
            <div className="flex flex-col gap-2 h-full">
              <div className="bg-light-350 w-full rounded-md max-h-40 py-4 flex flex-col justify-center items-center text-center gap-3">
                <CheckSuccessGreen />

                <h3 className="text-brand text-md font-semibold">
                  {t("book_exam.steps.four.mobile_title")}
                </h3>
              </div>
            </div>
          )
        )}
      </div>
    </div>
  );
}

